// Editor for the brand-keyed palettes stored in
// species.brand_color_light / brand_color_dark (JSONB).
//
// Shape edited:
//   { "cherry":       { "50": "#...", ..., "900": "#..." },
//     "fruitmarkets": { "50": "#...", ..., "900": "#..." } }
//
// Stops mirror the brand stops used at runtime (Tailwind palette).

import React from 'react';
import { Button, Input, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { BRANDS, type BrandKey } from 'common/src/utils/branding';

type Stop = '50' | '100' | '300' | '500' | '700' | '900';
type Palette = Record<Stop, string>;
type BrandedPalette = Partial<Record<BrandKey, Palette>>;

const STOPS: Stop[] = ['50', '100', '300', '500', '700', '900'];
const BRAND_KEYS = Object.keys(BRANDS) as BrandKey[];

const DEFAULT_LIGHT: Palette = {
  '50':  '#fff0f5',
  '100': '#ffabc5',
  '300': '#ff7f9f',
  '500': '#ff5379',
  '700': '#BE123C',
  '900': '#9f1239',
};

const DEFAULT_DARK: Palette = {
  '50':  '#2a0b13',
  '100': '#4c1127',
  '300': '#9f1239',
  '500': '#ff5379',
  '700': '#ff2753',
  '900': '#ff7f9f',
};

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

function isHex(value: string): boolean {
  return HEX_RE.test(value.trim());
}

function normalizeHex(value: string): string {
  const v = value.trim();
  return v.startsWith('#') ? v : `#${v}`;
}

function isPalette(value: unknown): value is Palette {
  if (!value || typeof value !== 'object') return false;
  return STOPS.every(
    (s) => typeof (value as Record<string, unknown>)[s] === 'string'
  );
}

function getPalette(
  branded: BrandedPalette | null | undefined,
  brand: BrandKey,
  fallback: Palette
): Palette {
  if (!branded) return { ...fallback };
  const direct = branded[brand];
  if (isPalette(direct)) return { ...direct };
  // Fallback to the cherry sub-palette if it exists.
  const cherry = branded.cherry;
  if (isPalette(cherry)) return { ...cherry };
  return { ...fallback };
}

type Props = {
  /** Current brand_color_light JSONB value. */
  light: BrandedPalette | null | undefined;
  /** Current brand_color_dark JSONB value. */
  dark: BrandedPalette | null | undefined;
  onChange: (next: {
    light: Record<BrandKey, Palette>;
    dark: Record<BrandKey, Palette>;
  }) => void;
};

export default function SpeciesBrandPaletteEditor({ light, dark, onChange }: Props) {
  // Always render every brand key so the JSONB keeps a complete shape after edit.
  const lightByBrand = BRAND_KEYS.reduce<Record<BrandKey, Palette>>(
    (acc, brand) => {
      acc[brand] = getPalette(light, brand, DEFAULT_LIGHT);
      return acc;
    },
    {} as Record<BrandKey, Palette>
  );
  const darkByBrand = BRAND_KEYS.reduce<Record<BrandKey, Palette>>(
    (acc, brand) => {
      acc[brand] = getPalette(dark, brand, DEFAULT_DARK);
      return acc;
    },
    {} as Record<BrandKey, Palette>
  );

  const updateStop = (
    mode: 'light' | 'dark',
    brand: BrandKey,
    stop: Stop,
    rawValue: string
  ) => {
    const value = normalizeHex(rawValue);
    const baseLight = mode === 'light'
      ? { ...lightByBrand, [brand]: { ...lightByBrand[brand], [stop]: value } }
      : lightByBrand;
    const baseDark = mode === 'dark'
      ? { ...darkByBrand, [brand]: { ...darkByBrand[brand], [stop]: value } }
      : darkByBrand;
    onChange({ light: baseLight, dark: baseDark });
  };

  const copyFromCherry = (target: BrandKey) => {
    if (target === 'cherry') return;
    onChange({
      light: { ...lightByBrand, [target]: { ...lightByBrand.cherry } },
      dark: { ...darkByBrand, [target]: { ...darkByBrand.cherry } },
    });
  };

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <h3 className='text-base font-semibold'>Colores de marca</h3>
        <p className='text-xs text-default-500'>
          Paleta usada por la app cuando se selecciona esta especie. Stops en
          formato Tailwind (50–900); el 700 es el color primario.
        </p>
      </div>

      {BRAND_KEYS.map((brand) => (
        <div
          key={brand}
          className='border border-default-200 rounded-lg p-3 dark:border-default-100'
        >
          <div className='flex items-center justify-between mb-2'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold'>{BRANDS[brand].name}</span>
              <span className='text-xs text-default-500'>({brand})</span>
            </div>
            {brand !== 'cherry' && (
              <Tooltip content='Copiar paleta de cherry'>
                <Button
                  size='sm'
                  variant='flat'
                  startContent={<Icon icon='tabler:copy' />}
                  onClick={() => copyFromCherry(brand)}
                >
                  Copiar de cherry
                </Button>
              </Tooltip>
            )}
          </div>

          <div className='grid grid-cols-2 gap-3'>
            {(['light', 'dark'] as const).map((mode) => {
              const palette = mode === 'light' ? lightByBrand[brand] : darkByBrand[brand];
              return (
                <div key={mode}>
                  <div className='text-xs font-medium uppercase tracking-wide text-default-500 mb-1'>
                    {mode === 'light' ? 'Light mode' : 'Dark mode'}
                  </div>
                  <div className='flex flex-col gap-1.5'>
                    {STOPS.map((stop) => {
                      const value = palette[stop] ?? '';
                      const valid = isHex(value);
                      return (
                        <div key={stop} className='flex items-center gap-2'>
                          <span className='w-10 text-xs text-default-500 font-mono'>
                            {stop}
                          </span>
                          <input
                            type='color'
                            value={valid ? normalizeHex(value) : '#000000'}
                            onChange={(e) =>
                              updateStop(mode, brand, stop, e.target.value)
                            }
                            className='h-8 w-10 rounded cursor-pointer border border-default-200 bg-transparent'
                            aria-label={`${brand} ${mode} ${stop}`}
                          />
                          <Input
                            size='sm'
                            value={value}
                            onChange={(e) =>
                              updateStop(mode, brand, stop, e.target.value)
                            }
                            isInvalid={!valid && value.length > 0}
                            classNames={{ input: 'font-mono text-xs' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
